'use client';

import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Badge,
  BodyDefault,
  HeadingItem,
  MetaDefault,
  Tabs,
} from '@terra/ui-design-system';
import { BrowserIcon, Database01Icon } from '@terra/ui-icons';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useEndpointTabs, useBottomPanel } from '../../store/layoutStore';
import { TabScopeProvider, useTabScope } from '../../contexts/TabScopeContext';
import { RequestsContent } from './RequestsContent';
import { SignalsContent } from './SignalsContent';
import { FindingsContent } from './FindingsContent';
import { TracesContent } from './TracesContent';
import { ReportsContent } from './ReportsContent';
import { ScopingContent } from './ScopingContent';
import { CodeModule } from '../CodeModule';
import { AssetsTabWrapper } from './AssetsTabWrapper';
import { MemoriesContent } from './MemoriesContent';
import { ErrorBoundary } from '../ErrorBoundary';
import { useAppTargetId } from '../../store/projectStore';
import type { Tab } from '../../store/layoutStore';

interface ApplicationOverviewTabProps {
  tab: Tab;
}

export function ApplicationOverviewTab({ tab }: ApplicationOverviewTabProps) {
  const appTargetId = useAppTargetId();
  const parentTabScopeId = useTabScope();
  const { selectedTab, setTab } = useEndpointTabs('app-overview');
  const applicationOverviewPaneScopeId = React.useMemo(() => {
    if (!parentTabScopeId || !appTargetId) {
      return undefined;
    }

    return `${parentTabScopeId}/subTab_${selectedTab}`;
  }, [parentTabScopeId, appTargetId, selectedTab]);
  // Set current endpoint ID to undefined for bottom panel context
  const { setCurrentEndpointId } = useBottomPanel();

  React.useEffect(() => {
    setCurrentEndpointId(undefined);

    return () => {
      setCurrentEndpointId(undefined);
    };
  }, [setCurrentEndpointId]);
  // Fetch application data
  const { data: applicationData, isLoading } = useQuery({
    queryKey: ['application-details', appTargetId],
    queryFn: async () => {
      if (!appTargetId) {
        throw new Error('No target ID found');
      }

      const result = await terraProxyApiClient.getApplicationById(appTargetId);

      return result;
    },
    enabled: !!appTargetId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  // Show loading state
  if (isLoading || !applicationData) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <BodyDefault color="fg.muted">Loading application data...</BodyDefault>
      </Box>
    );
  }

  const appName = applicationData.name || 'Target Overview';

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        {/* Header */}
        <Box
          px="4"
          py="3"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          bg="bg.default"
        >
          <VStack gap="2" alignItems="flex-start" w="full">
            <HStack gap="3" alignItems="center" w="full">
              <BrowserIcon size={20} />
              <HeadingItem
                color="fg.default"
                flex="1"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {appName}
              </HeadingItem>
              <Badge variant="outline" size="sm">
                Application View
              </Badge>
            </HStack>

            <HStack gap="4" alignItems="center" w="full">
              <HStack gap="1" alignItems="center">
                <Database01Icon size={14} />
                <MetaDefault color="fg.muted">
                  Viewing data across all endpoints, domains, and paths
                </MetaDefault>
              </HStack>
            </HStack>
          </VStack>
        </Box>

        {/* Nested scope per inner pane — see `applicationOverviewPaneScopeId` memo. */}
        <TabScopeProvider scopeId={applicationOverviewPaneScopeId}>
          <Box flex="1" overflow="hidden">
            <Tabs.Root
              value={selectedTab}
              size="sm"
              w="full"
              h="full"
              display="flex"
              flexDirection="column"
              overflow="hidden"
              lazyMount
              unmountOnExit
            >
              <Tabs.List justifyContent="flex-start">
                <Tabs.Trigger
                  value="scoping"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('scoping')}
                >
                  Scoping
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="assets"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('assets')}
                >
                  Assets
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="requests"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('requests')}
                >
                  Tracked Requests
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="signals"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('signals')}
                >
                  Signals
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="findings"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('findings')}
                >
                  Findings
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="executions"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('executions')}
                >
                  Executions
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="reports"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('reports')}
                >
                  Reports
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="code"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('code')}
                >
                  Code
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="memories"
                  fontSize="sm"
                  px="3"
                  py="2"
                  onClick={() => setTab('memories')}
                >
                  Memories
                </Tabs.Trigger>
                <Tabs.Indicator />
              </Tabs.List>

              <ErrorBoundary
                key={selectedTab}
                scopeId={`application-overview-${selectedTab}`}
                onClose={() => setTab('scoping')}
                dismissActionLabel="Go to Scoping"
              >
                <Tabs.Content value="scoping" flex="1" overflow="hidden" pt={0}>
                  <ScopingContent />
                </Tabs.Content>

                <Tabs.Content value="assets" flex="1" overflow="hidden" pt={0}>
                  <Box h="full" w="full" overflow="hidden">
                    <AssetsTabWrapper />
                  </Box>
                </Tabs.Content>

                <Tabs.Content
                  value="requests"
                  w="full"
                  h="full"
                  overflow="hidden"
                  pt={0}
                >
                  <Box h="full" w="full">
                    <RequestsContent tabId={tab.id} />
                  </Box>
                </Tabs.Content>

                <Tabs.Content value="signals" flex="1" overflow="hidden" pt={0}>
                  <Box h="full" w="full" overflow="hidden">
                    <SignalsContent />
                  </Box>
                </Tabs.Content>

                <Tabs.Content
                  value="findings"
                  flex="1"
                  overflow="hidden"
                  pt={0}
                >
                  <Box h="full" w="full" overflow="hidden">
                    <FindingsContent />
                  </Box>
                </Tabs.Content>

                <Tabs.Content
                  value="executions"
                  flex="1"
                  overflow="hidden"
                  pt={0}
                >
                  <Box h="full" w="full" overflow="hidden">
                    <TracesContent />
                  </Box>
                </Tabs.Content>

                <Tabs.Content value="reports" flex="1" overflow="hidden" pt={0}>
                  <Box h="full" w="full" overflow="hidden">
                    <ReportsContent />
                  </Box>
                </Tabs.Content>

                <Tabs.Content value="code" flex="1" overflow="hidden" pt={0}>
                  <Box h="full" w="full" overflow="hidden">
                    <CodeModule />
                  </Box>
                </Tabs.Content>

                <Tabs.Content
                  value="memories"
                  flex="1"
                  overflow="hidden"
                  pt={0}
                >
                  <Box h="full" w="full" overflow="hidden">
                    <MemoriesContent />
                  </Box>
                </Tabs.Content>
              </ErrorBoundary>
            </Tabs.Root>
          </Box>
        </TabScopeProvider>
      </VStack>
    </Box>
  );
}
